import { Injectable } from '@angular/core';
import { CategoryPoint } from '../pages/custom-route/interfaces/custom-route.response';
import { PointType, pointTypeMap } from '../pages/custom-route/interfaces/custom-route.interface';
import { DEFAULT_LANGUAGE, MAX_IMAGES_PER_CATEGORY } from '../config/constans';

/**
 * Service to generate default image file names for points in the custom route
 * Provides fallback images cycling between 1-4 based on the index
 */
@Injectable({
  providedIn: 'root'
})
export class DefaultImgService {
  /**
   * Returns the default image file name for a card, cycling between 1-4 based on index.
   * @param index - The index of the card (0-based)
   * @param category - The category object or array of categories
   * @param type - The type of the point (event or interest point)
   * @returns The file path to the default image, or empty string if invalid
   */
  public getDefaultImgFileName(
    index: number, 
    category: CategoryPoint | CategoryPoint[] | any, 
    type: PointType
  ): string {
    // Validate inputs
    if (!this.isValidIndex(index)) {
      console.warn('[DefaultImgService] Invalid index provided:', index);
      return '';
    }

    if (!this.isValidType(type)) {
      console.warn('[DefaultImgService] Invalid type provided:', type);
      return '';
    }

    // Handle event type (doesn't need category)
    if (type === pointTypeMap.event) {
      return this.getEventDefaultImgFileName(this.calculateCyclicIndex(index));
    }

    // Extract and validate category
    const categoryObj = this.extractCategoryObject(category);
    if (!categoryObj) {
      console.warn('[DefaultImgService] Could not extract valid category object from:', category);
      return '';
    }

    const categoryNameSlug = this.getCategoryNameSlug(categoryObj);
    if (!categoryNameSlug) {
      console.warn('[DefaultImgService] Could not extract nameSlug from category:', categoryObj);
      return '';
    }

    return this.getInterestPointDefaultImgFileName(categoryNameSlug, index + 1);
  }

  /**
   * Returns the default image file name for an event, cycling between 1-4 based on index.
   * @param index - The index to use for cycling (1-based)
   * @returns The file path to the default event image
   */
  public getEventDefaultImgFileName(index: number): string {
    const imageNumber = this.calculateImageNumber(index);
    return `/assets/imgs/default/event/event-${imageNumber}.webp`;
  }

  /**
   * Returns the default image file name for an interest point, cycling between 1-4 based on itemId.
   * @param nameSlug - The category name slug (e.g., 'cultural', 'gastronomy')
   * @param itemId - The item ID to use for cycling (1-based)
   * @returns The file path to the default interest point image
   */
  public getInterestPointDefaultImgFileName(nameSlug: string, itemId: number): string {
    if (!nameSlug) {
      console.warn('[DefaultImgService] Empty nameSlug provided');
      return '';
    }

    const imageNumber = this.calculateImageNumber(itemId);
    return `/assets/imgs/default/category/${nameSlug}/${nameSlug}-${imageNumber}.webp`;
  }

  /**
   * Extracts a valid category object from either a single category, array, or partial object
   * @param category - The category data (can be object, array, or partial)
   * @returns A valid CategoryPoint object or null
   */
  private extractCategoryObject(category: CategoryPoint | CategoryPoint[] | any): CategoryPoint | null {
    if (!category) {
      return null;
    }

    // Handle array of categories - take first valid one
    if (Array.isArray(category)) {
      const firstCategory = category[0];
      return this.isValidCategoryObject(firstCategory) ? firstCategory : null;
    }
    
    // Handle single category object
    return this.isValidCategoryObject(category) ? category : null;
  }

  /**
   * Validates if the object is a complete CategoryPoint with required properties
   * @param obj - The object to validate
   * @returns True if the object is a valid CategoryPoint
   */
  private isValidCategoryObject(obj: any): obj is CategoryPoint {
    return (
      obj !== null &&
      obj !== undefined &&
      typeof obj === 'object' &&
      'id' in obj &&
      'nameSlug' in obj &&
      obj.nameSlug !== undefined &&
      typeof obj.nameSlug === 'object' &&
      Object.keys(obj.nameSlug).length > 0
    );
  }

  /**
   * Extracts the category name slug from a CategoryPoint object
   * @param category - The category object
   * @returns The nameSlug string or null if not found
   */
  private getCategoryNameSlug(category: CategoryPoint): string | null {
    if (!category.nameSlug) {
      return null;
    }

    // Try to get the slug in the default language
    const slug = category.nameSlug[DEFAULT_LANGUAGE];
    if (slug) {
      return slug;
    }

    // Fallback: try to get any available language
    const availableLanguages = Object.keys(category.nameSlug);
    if (availableLanguages.length > 0) {
      const firstAvailableLanguage = availableLanguages[0];
      console.warn(
        `[DefaultImgService] Default language '${DEFAULT_LANGUAGE}' not found, using '${firstAvailableLanguage}'`
      );
      return category.nameSlug[firstAvailableLanguage];
    }

    return null;
  }

  /**
   * Validates if the index is a valid number
   * @param index - The index to validate
   * @returns True if the index is valid
   */
  private isValidIndex(index: number): boolean {
    return typeof index === 'number' && !isNaN(index) && index >= 0;
  }

  /**
   * Validates if the type is a valid PointType
   * @param type - The type to validate
   * @returns True if the type is valid
   */
  private isValidType(type: PointType): boolean {
    return type !== null && type !== undefined;
  }

  /**
   * Calculates the cyclic index (1-4) based on the input index
   * @param index - The input index (0-based)
   * @returns A number between 1 and MAX_IMAGES_PER_CATEGORY
   */
  private calculateCyclicIndex(index: number): number {
    return (index % MAX_IMAGES_PER_CATEGORY) + 1;
  }

  /**
   * Calculates the image number (1-4) based on the item ID
   * @param itemId - The item ID (1-based)
   * @returns A number between 1 and MAX_IMAGES_PER_CATEGORY
   */
  private calculateImageNumber(itemId: number): number {
    return ((itemId - 1) % MAX_IMAGES_PER_CATEGORY) + 1;
  }
}